/**
 * useThreadDrag — drag a row onto a section (2026-09-09). Native HTML5 drag,
 * offered only where a pointer can hover (a phone keeps the row ⋯ path);
 * every section header and body is a drop target; dropping on a section
 * FILES the thread under that section's id — a built-in included — through
 * the same `moveThread` the menus use, so undo works the same way.
 */
import { useCallback, useMemo, useState, type DragEvent } from 'react';
import { finePointerAvailable, type ThreadOrganizeApi } from './threadOrganize';

export const THREAD_DRAG_MIME = 'application/x-prest0n-thread';

export interface ThreadDragRowProps {
  draggable?: boolean;
  onDragStart?: (e: DragEvent<HTMLElement>) => void;
  onDragEnd?: (e: DragEvent<HTMLElement>) => void;
}

export interface ThreadDragSectionProps {
  onDragOver?: (e: DragEvent<HTMLElement>) => void;
  onDragLeave?: (e: DragEvent<HTMLElement>) => void;
  onDrop?: (e: DragEvent<HTMLElement>) => void;
}

export interface ThreadDragApi {
  enabled: boolean;
  dragging: string | null;
  /** The section id a dragged row is over. */
  over: string | null;
  rowProps: (threadId: string, disabled?: boolean) => ThreadDragRowProps;
  sectionProps: (sectionId: string) => ThreadDragSectionProps;
}

export function useThreadDrag(organize: ThreadOrganizeApi | null | undefined): ThreadDragApi {
  const enabled = !!organize && finePointerAvailable();
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const rowProps = useCallback((threadId: string, disabled?: boolean): ThreadDragRowProps => {
    if (!enabled || disabled) return {};
    return {
      draggable: true,
      onDragStart: (e) => {
        try {
          e.dataTransfer.setData(THREAD_DRAG_MIME, threadId);
          e.dataTransfer.effectAllowed = 'move';
        } catch { /* a browser without dataTransfer setData — the state still carries the id */ }
        setDragging(threadId);
      },
      onDragEnd: () => {
        setDragging(null);
        setOver(null);
      },
    };
  }, [enabled]);

  const sectionProps = useCallback((sectionId: string): ThreadDragSectionProps => {
    if (!enabled) return {};
    const carriesThread = (e: DragEvent<HTMLElement>) => {
      if (dragging) return true;
      try { return Array.from(e.dataTransfer.types || []).includes(THREAD_DRAG_MIME); } catch { return false; }
    };
    return {
      onDragOver: (e) => {
        if (!carriesThread(e)) return;
        e.preventDefault();
        try { e.dataTransfer.dropEffect = 'move'; } catch { /* read-only in some browsers */ }
        setOver((current) => (current === sectionId ? current : sectionId));
      },
      onDragLeave: () => {
        setOver((current) => (current === sectionId ? null : current));
      },
      onDrop: (e) => {
        if (!carriesThread(e)) return;
        e.preventDefault();
        let id = '';
        try { id = e.dataTransfer.getData(THREAD_DRAG_MIME) || ''; } catch { id = ''; }
        const threadId = id || dragging || '';
        setOver(null);
        setDragging(null);
        if (threadId && organize) organize.moveThread(threadId, sectionId);
      },
    };
  }, [enabled, dragging, organize]);

  return useMemo(() => ({ enabled, dragging, over, rowProps, sectionProps }), [enabled, dragging, over, rowProps, sectionProps]);
}
